import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

/**
 * Light / dark appearance for the /account app.
 *
 * The whole design system keys on `data-theme` on <html> (see
 * src/styles/compositor.css and src/styles/brands.css), so this provider only
 * has to own the attribute — no component reads the theme directly.
 *
 * 'system' follows the OS and keeps following it when the OS flips; 'light' and
 * 'dark' pin the choice. The preference is per-browser (localStorage), not
 * per-account, so it survives sign-out.
 *
 * NOTE: Tailwind's `dark:` variant is bound to `[data-theme="dark"]` in
 * tailwind.config.js, so it tracks this attribute rather than the OS.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'smagent.theme';

function readStoredPreference(): ThemePreference {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
}

function prefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersDark() ? 'dark' : 'light';
  return preference;
}

interface ThemeContextValue {
  /** What the user chose ('system' included). */
  preference: ThemePreference;
  /** What that currently resolves to — always 'light' or 'dark'. */
  theme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Flip between light and dark, pinning the result. */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(readStoredPreference()));

  // Drive the attribute the design system keys on.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Re-resolve when the preference changes, and keep following the OS while the
  // preference is 'system'.
  useEffect(() => {
    setTheme(resolve(preference));
    if (preference !== 'system') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setTheme(prefersDark() ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = (next: ThemePreference) => {
    localStorage.setItem(STORAGE_KEY, next);
    setPreferenceState(next);
  };

  const toggle = () => setPreference(theme === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
